// frontend/src/types/maintenance.ts

/**
 * TypeScript type definitions for the Maintenance feature.
 * Matches the backend Django models and serializers.
 */

import type { Contact, User } from './index';

// ============================================================================
// Enums and Constants
// ============================================================================

export type MaintenanceFrequency =
  | 'monthly'
  | 'quarterly'
  | 'semi_annual'
  | 'annual'
  | 'bi_annual'
  | 'custom';

export type ContractStatus =
  | 'draft'
  | 'pending'
  | 'active'
  | 'paused'
  | 'expired'
  | 'cancelled';

export type AppointmentStatus =
  | 'scheduled'
  | 'in_progress'
  | 'completed'
  | 'cancelled'
  | 'rescheduled'
  | 'no_show';

export type PhotoType =
  | 'pre_repair'
  | 'post_repair'
  | 'damage'
  | 'general';

export type RepairSeverity = 'green' | 'yellow' | 'red';

export const FREQUENCY_LABELS: Record<MaintenanceFrequency, string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  semi_annual: 'Semi-Annual (Every 6 months)',
  annual: 'Annual',
  bi_annual: 'Bi-Annual (Every 2 years)',
  custom: 'Custom',
};

export const CONTRACT_STATUS_LABELS: Record<ContractStatus, string> = {
  draft: 'Draft',
  pending: 'Pending Approval',
  active: 'Active',
  paused: 'Paused',
  expired: 'Expired',
  cancelled: 'Cancelled',
};

export const APPOINTMENT_STATUS_LABELS: Record<AppointmentStatus, string> = {
  scheduled: 'Scheduled',
  in_progress: 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
  rescheduled: 'Rescheduled',
  no_show: 'No Show',
};

export const PHOTO_TYPE_LABELS: Record<PhotoType, string> = {
  pre_repair: 'Pre-Repair',
  post_repair: 'Post-Repair',
  damage: 'Damage Found',
  general: 'General Inspection',
};

export const SEVERITY_LABELS: Record<RepairSeverity, string> = {
  green: 'Good Condition',
  yellow: 'Minor Issue - Monitor',
  red: 'Needs Repair',
};

export const SEVERITY_COLORS: Record<RepairSeverity, string> = {
  green: '#22c55e',
  yellow: '#eab308',
  red: '#ef4444',
};

// ============================================================================
// Main Types
// ============================================================================

export interface ChecklistMinimal {
  id: number;
  title: string;
  description?: string;
  steps_count: number;
}

export interface MaintenanceContractTemplate {
  id: number;
  organization: string;
  name: string;
  description: string;
  frequency: MaintenanceFrequency;
  fee_per_appointment: string;
  inspection_checklist: string[];
  included_services: string[];
  is_active: boolean;
  duration_months: number;
  auto_renew: boolean;
  created_at: string;
  updated_at: string;
}

export interface MaintenanceContractTemplateForm {
  name: string;
  description?: string;
  frequency: MaintenanceFrequency;
  fee_per_appointment: string;
  inspection_checklist?: string[];
  included_services?: string[];
  is_active?: boolean;
  duration_months?: number;
  auto_renew?: boolean;
}

export interface MaintenanceContract {
  renewal_owner?: number | null;
  renewal_owner_name?: string;
  renewal_decision?: 'pending' | 'renew' | 'end';
  next_action?: string;
  next_action_date?: string | null;
  id: number;
  organization: string;
  contract_number: string;
  contact: Contact;
  contact_id?: number;
  template: MaintenanceContractTemplate | null;
  template_id?: number | null;
  proposal: number | null;
  checklist: ChecklistMinimal | null;
  checklist_id?: number | null;
  property_address_line1: string;
  property_address_line2: string;
  property_city: string;
  property_state: string;
  property_zip: string;
  property_latitude: string | null;
  property_longitude: string | null;
  status: ContractStatus;
  start_date: string;
  end_date: string | null;
  custom_frequency: MaintenanceFrequency | '';
  custom_fee: string | null;
  custom_checklist: string[];
  auto_renew: boolean;
  next_billing_date: string | null;
  nte: string | null;
  notes: string;
  effective_frequency: MaintenanceFrequency;
  effective_fee: string;
  effective_checklist: string[];
  full_property_address: string;
  created_at: string;
  updated_at: string;
}

export interface MaintenanceContractListItem {
  renewal_owner?: number | null;
  renewal_owner_name?: string;
  renewal_decision?: 'pending' | 'renew' | 'end';
  next_action?: string;
  next_action_date?: string | null;
  id: number;
  contract_number: string;
  contact: number;
  contact_name: string;
  template: number | null;
  template_name: string | null;
  checklist_id: number | null;
  checklist_title: string | null;
  status: ContractStatus;
  start_date: string;
  end_date: string | null;
  effective_frequency: MaintenanceFrequency;
  effective_fee: string;
  full_property_address: string;
  appointment_count: number;
  auto_renew: boolean;
  nte: string | null;
  created_at: string;
}

export interface MaintenanceContractForm {
  renewal_owner?: number | null;
  renewal_owner_name?: string;
  renewal_decision?: 'pending' | 'renew' | 'end';
  next_action?: string;
  next_action_date?: string | null;
  contact_id: number;
  template_id?: number | null;
  checklist_id?: number | null;
  property_address_line1: string;
  property_address_line2?: string;
  property_city: string;
  property_state: string;
  property_zip: string;
  property_latitude?: string | null;
  property_longitude?: string | null;
  status?: ContractStatus;
  start_date: string;
  end_date?: string | null;
  custom_frequency?: MaintenanceFrequency | '';
  custom_fee?: string | null;
  custom_checklist?: string[];
  auto_renew?: boolean;
  nte?: string | null;
  notes?: string;
}

export interface MaintenanceRepairItem {
  id: number;
  organization: string;
  appointment: number;
  estimate_line_item: number | null;
  title: string;
  description: string;
  location: string;
  system: string;
  solution: string;
  severity: RepairSeverity;
  severity_display: string;
  estimated_cost: string | null;
  repaired_on_site: boolean;
  repair_notes: string;
  repaired_at: string | null;
  photo_count: number;
  created_at: string;
  updated_at: string;
}

export interface MaintenanceRepairItemForm {
  appointment?: number;
  title: string;
  description?: string;
  location?: string;
  system?: string;
  solution?: string;
  severity: RepairSeverity;
  estimated_cost?: string | null;
  repaired_on_site?: boolean;
  repair_notes?: string;
}

export interface MaintenancePhoto {
  id: number;
  organization: string;
  appointment: number;
  repair_item: number | null;
  image: string;
  image_url: string;
  thumbnail: string | null;
  thumbnail_url: string | null;
  photo_type: PhotoType;
  photo_type_display: string;
  description: string;
  taken_at: string;
  latitude: string | null;
  longitude: string | null;
  created_at: string;
  updated_at: string;
}

export interface MaintenancePhotoForm {
  appointment: number;
  repair_item?: number | null;
  image: File;
  photo_type: PhotoType;
  description?: string;
  latitude?: string | null;
  longitude?: string | null;
}

export interface MaintenanceAppointment {
  id: number;
  organization: string;
  contract: MaintenanceContractListItem;
  contract_id?: number;
  assigned_to: User | null;
  assigned_to_id?: number | null;
  scheduled_date: string;
  scheduled_time_start: string | null;
  scheduled_time_end: string | null;
  status: AppointmentStatus;
  // Check-in
  checkin_time: string | null;
  checkin_latitude: string | null;
  checkin_longitude: string | null;
  checkin_accuracy: string | null;
  checkin_altitude: string | null;
  checkin_heading: string | null;
  checkin_speed: string | null;
  // Checkout
  checkout_time: string | null;
  checkout_latitude: string | null;
  checkout_longitude: string | null;
  checkout_accuracy: string | null;
  checkout_altitude: string | null;
  checkout_heading: string | null;
  checkout_speed: string | null;
  // Work
  work_summary: string;
  checklist_results: Record<string, boolean>;
  // Report
  report_generated: boolean;
  report_file: string | null;
  report_generated_at: string | null;
  // Signature
  customer_signature: string;
  customer_signed_at: string | null;
  // Notes
  internal_notes: string;
  // Related
  photos: MaintenancePhoto[];
  repair_items: MaintenanceRepairItem[];
  // Computed
  is_checked_in: boolean;
  is_checked_out: boolean;
  duration_minutes: number | null;
  created_at: string;
  updated_at: string;
}

export interface MaintenanceAppointmentListItem {
  id: number;
  contract: number;
  contract_number: string;
  contact_name: string;
  property_address: string;
  assigned_to: number | null;
  assigned_to_name: string | null;
  scheduled_date: string;
  scheduled_time_start: string | null;
  scheduled_time_end: string | null;
  status: AppointmentStatus;
  checkin_time: string | null;
  checkout_time: string | null;
  is_checked_in: boolean;
  is_checked_out: boolean;
  duration_minutes: number | null;
  photo_count: number;
  repair_item_count: number;
  report_generated: boolean;
  created_at: string;
}

export interface MaintenanceAppointmentForm {
  contract_id: number;
  assigned_to_id?: number | null;
  scheduled_date: string;
  scheduled_time_start?: string | null;
  scheduled_time_end?: string | null;
}

export interface GPSCheckinData {
  latitude: string;
  longitude: string;
  accuracy?: string;
  altitude?: string;
  heading?: string;
  speed?: string;
}

export interface GPSCheckoutData extends GPSCheckinData {
  work_summary?: string;
  checklist_results?: Record<string, boolean>;
}

export interface MaintenanceNotificationSettings {
  id: number;
  organization: string;
  notify_on_checkout: boolean;
  notify_on_report_generated: boolean;
  notify_on_repair_item_found: boolean;
  notification_recipients: User[];
  notification_recipient_ids?: number[];
  notify_roles: string[];
  notify_contract_owner: boolean;
  notify_assigned_user: boolean;
  send_customer_report: boolean;
  send_customer_reminder: boolean;
  customer_reminder_days: number;
  created_at: string;
  updated_at: string;
}

export interface MaintenanceNotificationSettingsForm {
  notify_on_checkout?: boolean;
  notify_on_report_generated?: boolean;
  notify_on_repair_item_found?: boolean;
  notification_recipient_ids?: number[];
  notify_roles?: string[];
  notify_contract_owner?: boolean;
  notify_assigned_user?: boolean;
  send_customer_report?: boolean;
  send_customer_reminder?: boolean;
  customer_reminder_days?: number;
}

// ============================================================================
// API Response Types
// ============================================================================

export interface CreateEstimateResponse {
  message: string;
  estimate_id: number;
  estimate_number: string;
  total_amount: string;
  item_count: number;
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// ============================================================================
// Filter Types
// ============================================================================

export interface ContractFilters {
  search?: string;
  renewal?: string;
  status?: ContractStatus;
  contact?: number;
  template?: number;
  start_date_from?: string;
  start_date_to?: string;
  page?: number;
}

export interface AppointmentFilters {
  status?: AppointmentStatus;
  contract?: number;
  assigned_to?: number;
  scheduled_date?: string;
  scheduled_date_from?: string;
  scheduled_date_to?: string;
  report_generated?: boolean;
  page?: number;
}

export interface MaintenanceAppointmentCalendarItem {
  id: number;
  title: string;
  start: string;
  end: string;
  type: 'maintenance';
  status: AppointmentStatus;
  contract_number: string;
  contact_name: string;
  property_address: string;
}

// ============================================================================
// Helper Functions
// ============================================================================

export const getStatusColor = (status: ContractStatus | AppointmentStatus): string => {
  const statusColors: Record<string, string> = {
    // Contract statuses
    draft: '#9ca3af',
    pending: '#f59e0b',
    active: '#22c55e',
    paused: '#6366f1',
    expired: '#ef4444',
    cancelled: '#dc2626',
    // Appointment statuses
    scheduled: '#3b82f6',
    in_progress: '#f59e0b',
    completed: '#22c55e',
    rescheduled: '#8b5cf6',
    no_show: '#ef4444',
  };
  return statusColors[status] || '#9ca3af';
};

export const formatDuration = (minutes: number | null): string => {
  if (!minutes) return '-';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (remainingMinutes === 0) return `${hours}h`;
  return `${hours}h ${remainingMinutes}m`;
};

export const formatAddress = (contract: MaintenanceContract | MaintenanceContractListItem): string => {
  if ('full_property_address' in contract) {
    return contract.full_property_address;
  }
  const c = contract as MaintenanceContract;
  const parts = [c.property_address_line1];
  if (c.property_address_line2) parts.push(c.property_address_line2);
  parts.push(`${c.property_city}, ${c.property_state} ${c.property_zip}`);
  return parts.join(', ');
};
